const CERTIFIED_CONTENT_SUFFIX = /\s*\[certified content[^\]]*\]\s*$/i;
const NO_LABEL_SUFFIX = /\s*(?:\(no label\)|no label)\s*$/i;

export type ClaimsCategoryLoadState = "idle" | "loading" | "loaded" | "error";

export type ClaimsCategoryMembershipReason =
  | "listed"
  | "not-loaded"
  | "load-error"
  | "no-listed-claims"
  | "not-listed";

export interface ClaimsCategoryMembershipResult {
  isValid: boolean;
  reason: ClaimsCategoryMembershipReason;
}

export type CertifiedWeightClaimsValidationReason =
  | "ok"
  | "no-certified-weight-rows"
  | "missing-claims";

export interface CertifiedWeightClaimsValidationResult {
  isValid: boolean;
  reason: CertifiedWeightClaimsValidationReason;
  missingClaimCount: number;
}

export const claimsCategoryKey = (value: unknown): string => {
  let text = String(value || "").trim().replace(/\s+/g, " ");
  text = text.replace(CERTIFIED_CONTENT_SUFFIX, "");
  text = text.replace(NO_LABEL_SUFFIX, "");
  const upper = text.toUpperCase();
  return upper.includes("MATERIALS MATTER") || upper.includes("MATERIAL MATTER")
    ? "MMS"
    : upper;
};

export const matchClaimsCategoryOption = (
  rawVal: unknown,
  options: readonly { value: string; label: string }[]
): string => {
  const valStr = String(rawVal || "").trim();
  if (!valStr || valStr.toLowerCase() === "na") return "";
  if (!Array.isArray(options) || options.length === 0) return valStr;

  const exact = options.find(
    (opt) => String(opt.value || "").trim().toLowerCase() === valStr.toLowerCase()
  );
  if (exact) return exact.value;

  const rawKey = claimsCategoryKey(valStr);
  const rawUpper = valStr.toUpperCase();

  const keyMatch = options.find((opt) => {
    const optVal = String(opt.value || "").trim();
    const optKey = claimsCategoryKey(optVal);
    const optUpper = optVal.toUpperCase();
    return (
      (rawKey && optKey && rawKey === optKey) ||
      optUpper.startsWith(rawUpper) ||
      rawUpper.startsWith(optUpper)
    );
  });

  return keyMatch ? keyMatch.value : valStr;
};

// DAT-501 "tc Product Claims Category": the rule "each value must be present
// on the Seller SC attached" was struck from the validation list, so
// membership is checked against the TC-standard-scoped claims list only.
export const validateClaimsCategoryMembership = (
  value: unknown,
  listedClaims: readonly unknown[],
  loadState: ClaimsCategoryLoadState
): ClaimsCategoryMembershipResult => {
  if (loadState === "error") {
    return { isValid: false, reason: "load-error" };
  }
  if (loadState !== "loaded") {
    return { isValid: false, reason: "not-loaded" };
  }

  const listedKeys = new Set(
    listedClaims.map(claimsCategoryKey).filter(Boolean)
  );
  if (listedKeys.size === 0) {
    return { isValid: false, reason: "no-listed-claims" };
  }

  return listedKeys.has(claimsCategoryKey(value))
    ? { isValid: true, reason: "listed" }
    : { isValid: false, reason: "not-listed" };
};

export const claimsCategoryMembershipError = (
  reason: ClaimsCategoryMembershipReason,
  rawMaterialCode: string,
  value: unknown
): string => {
  switch (reason) {
    case "not-loaded":
      return "Claims Categories are still loading. Please wait before saving.";
    case "load-error":
      return "Claims Categories could not be loaded. Please retry before saving.";
    case "no-listed-claims":
      return `No Claims Category is available for certified raw material ${rawMaterialCode} under the TC standards.`;
    case "not-listed":
      return `Claims Category "${String(value || "").trim()}" does not belong to the TC standards for certified raw material ${rawMaterialCode}. Please select a listed value.`;
    default:
      return "";
  }
};

export const certifiedWeightClaimFromRow = (row: any): string =>
  String(
    row?.tcProductClaimsCategory ||
      row?.claimsCategory ||
      row?.tcClaimsCategory ||
      row?.tcStandard ||
      row?.tcProductRawMaterialCodeClaimsCategory ||
      row?.tcProductRawMaterialCodeStandardLabel ||
      ""
  ).trim();

export const certifiedWeightRowsFromResponseData = (data: any): any[] =>
  Array.isArray(data?.response)
    ? data.response
    : Array.isArray(data)
    ? data
    : [];

// Structural check only: rows exist and every persisted row carries a Claims
// Category. Standard membership and the remaining DAT-501 rules are enforced
// by the backend submit validators (the Seller-SC-approval rule was struck).
export const validateCertifiedWeightClaims = (
  rows: readonly any[]
): CertifiedWeightClaimsValidationResult => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      isValid: false,
      reason: "no-certified-weight-rows",
      missingClaimCount: 0,
    };
  }

  const missingClaimCount = rows
    .map(certifiedWeightClaimFromRow)
    .filter((claim) => !claim).length;
  if (missingClaimCount > 0) {
    return { isValid: false, reason: "missing-claims", missingClaimCount };
  }

  return { isValid: true, reason: "ok", missingClaimCount: 0 };
};

export const certifiedWeightClaimsValidationError = (
  result: CertifiedWeightClaimsValidationResult
): string => {
  if (result.isValid) {
    return "";
  }

  if (result.reason === "no-certified-weight-rows") {
    return "Certified-weight Claims Categories could not be verified. Return to Product Information and save every certified product before continuing.";
  }

  return `${result.missingClaimCount} saved certified-weight ${
    result.missingClaimCount === 1 ? "row has" : "rows have"
  } no Claims Category. Return to Product Information, choose a claim for every certified raw material, and save the product again.`;
};

export const extractStandardFromClaim = (claim: unknown): string => {
  if (!claim) return "";
  let text = String(claim).trim().replace(/\s+/g, " ");
  if (!text || text.toLowerCase() === "na") return "";

  text = text.replace(CERTIFIED_CONTENT_SUFFIX, "");
  text = text.replace(NO_LABEL_SUFFIX, "");

  const upper = text.toUpperCase();
  if (
    upper.includes("MATERIALS MATTER") ||
    upper.includes("MATERIAL MATTER") ||
    upper.includes("MATERIALS STANDARD") ||
    upper.includes("MATERIAL STANDARD") ||
    upper.startsWith("MS ") ||
    upper === "MS" ||
    upper === "MMS"
  ) {
    return "MMS";
  }
  if (upper.startsWith("GRS") || upper.includes("GLOBAL RECYCLED STANDARD")) return "GRS";
  if (upper.startsWith("RCS") || upper.includes("RECYCLED CLAIM STANDARD")) return "RCS";
  if (upper.startsWith("RWS") || upper.includes("RESPONSIBLE WOOL STANDARD")) return "RWS";
  if (upper.startsWith("RMS") || upper.includes("RESPONSIBLE MOHAIR STANDARD")) return "RMS";
  if (upper.startsWith("RAS") || upper.includes("RESPONSIBLE ALPACA STANDARD")) return "RAS";
  if (upper.startsWith("OCS") || upper.includes("ORGANIC CONTENT STANDARD")) return "OCS";
  if (upper.startsWith("RDS") || upper.includes("RESPONSIBLE DOWN STANDARD")) return "RDS";
  if (upper.startsWith("GOTS") || upper.includes("GLOBAL ORGANIC TEXTILE STANDARD")) return "GOTS";

  const parts = upper.split(/[\s-\(]/);
  return parts[0]?.trim() || upper;
};

export interface ClaimsCombinationValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export const INVALID_MATERIALS_STANDARD_COMBINATIONS = ["GRS", "RCS", "RWS", "RMS", "RAS"] as const;

export const validateClaimsCategoryCombinationsForSet = (
  claims: readonly string[],
  context: "product-without-components" | "component",
  name?: string
): ClaimsCombinationValidationResult => {
  const standards = new Set<string>();
  for (const claim of claims) {
    const std = extractStandardFromClaim(claim);
    if (std) standards.add(std);
  }

  // 1. Materials Standard cannot be combined with GRS, RCS, RWS, RMS, RAS
  if (standards.has("MMS")) {
    const conflicting = INVALID_MATERIALS_STANDARD_COMBINATIONS.filter((std) => standards.has(std));
    if (conflicting.length > 0) {
      const subject = context === "component" ? (name ? `Component ${name}` : "A component") : "A product";
      return {
        isValid: false,
        errorMessage: `${subject} cannot have a Materials Standard claims category together with ${conflicting.join(", ")}.`,
      };
    }
  }

  // 2. GRS and RCS cannot be selected simultaneously (within product without components or within same component)
  if (standards.has("GRS") && standards.has("RCS")) {
    const subject = context === "component" ? (name ? `Component ${name}` : "A component") : "A product without components";
    return {
      isValid: false,
      errorMessage: `${subject} cannot have both GRS and RCS claims categories simultaneously.`,
    };
  }

  return { isValid: true };
};

export const validateProductClaimsCombinations = (product: {
  isWithComponent?: boolean;
  rawMaterials?: readonly any[];
  certifiedWeights?: readonly any[];
  components?: readonly any[];
  articleNo?: string;
}): ClaimsCombinationValidationResult => {
  const isWithComp =
    product.isWithComponent === true ||
    (Array.isArray(product.components) && product.components.length > 0);

  const extractClaimsFromRows = (
    rawMaterials: readonly any[] = [],
    certifiedWeights: readonly any[] = []
  ): string[] => {
    const claims: string[] = [];
    for (const rm of rawMaterials) {
      const c =
        rm?.claimsCategory ||
        rm?.tcProductClaimsCategory ||
        rm?.tcProductComponentClaimsCategory;
      if (c && String(c).trim() && String(c).trim().toLowerCase() !== "na") {
        claims.push(String(c).trim());
      }
    }
    for (const cw of certifiedWeights) {
      const c = certifiedWeightClaimFromRow(cw);
      if (c && String(c).trim() && String(c).trim().toLowerCase() !== "na") {
        claims.push(String(c).trim());
      }
    }
    return claims;
  };

  if (!isWithComp) {
    const productClaims = extractClaimsFromRows(
      product.rawMaterials,
      product.certifiedWeights
    );
    return validateClaimsCategoryCombinationsForSet(
      productClaims,
      "product-without-components",
      product.articleNo
    );
  }

  // Product With Components:
  // a) Product-level Materials Standard check across ALL components (and product-level claims)
  const allProductClaims: string[] = extractClaimsFromRows(
    product.rawMaterials,
    product.certifiedWeights
  );
  const comps = product.components || [];
  for (const comp of comps) {
    const compRm =
      comp?.tcProductComponentRawMaterialInformation || comp?.rawMaterials || [];
    const compCw =
      comp?.tcProductComponentCertifiedWeightInformation || comp?.certifiedWeights || [];
    allProductClaims.push(...extractClaimsFromRows(compRm, compCw));
  }

  const allStandards = new Set<string>();
  for (const claim of allProductClaims) {
    const std = extractStandardFromClaim(claim);
    if (std) allStandards.add(std);
  }

  if (allStandards.has("MMS")) {
    const conflicting = INVALID_MATERIALS_STANDARD_COMBINATIONS.filter((std) =>
      allStandards.has(std)
    );
    if (conflicting.length > 0) {
      return {
        isValid: false,
        errorMessage: `A product cannot have a Materials Standard claims category together with ${conflicting.join(", ")}.`,
      };
    }
  }

  // b) Component-level GRS/RCS (and MMS) check for EACH component independently
  for (let i = 0; i < comps.length; i++) {
    const comp = comps[i];
    const compNo =
      comp?.tcProductComponentNo || comp?.componentNo || `C${i + 1}`;
    const compRm =
      comp?.tcProductComponentRawMaterialInformation || comp?.rawMaterials || [];
    const compCw =
      comp?.tcProductComponentCertifiedWeightInformation || comp?.certifiedWeights || [];
    const compClaims = extractClaimsFromRows(compRm, compCw);

    const compValidation = validateClaimsCategoryCombinationsForSet(
      compClaims,
      "component",
      compNo
    );
    if (!compValidation.isValid) {
      return compValidation;
    }
  }

  return { isValid: true };
};

